import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { isAnyObject } from "@-/com.npmjs.is-what";
import * as SQLite from "@-/com.npmjs.expo-sqlite";
import type { AnySQLiteDatabase } from "@-/com.npmjs.expo-sqlite";
import {
  openProxiedActorTursoConnection,
  openProxiedGuestTursoConnection,
  wipeTursoDatabaseFiles,
  type ActorTursoSyncConnection,
  type ProxiedTursoConnection,
  type ProxiedTursoSyncOptions,
} from "@-/dev.improve.haniverse.lib.database.engine/turso-sync";
import {
  actorSchema,
  actorRelations,
} from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import actorMigrations from "@-/dev.improve.haniverse.database.schemas.actor.migrations";
import { getDeviceAccount, setDeviceAccount } from "@-/dev.improve.haniverse.lib.device-settings";
import { ACTOR_LEADER_RESOURCE_ID, GUEST_LEADER_RESOURCE_ID } from "#actor-database-resource-ids";
import {
  isLegacyDeviceAccount,
  resolveActorSyncGatewayUrl,
  resolveActorSyncUrl,
} from "#resolve-actor-sync-url";
import { getBetterAuthBaseUrl } from "@-/dev.improve.haniverse.lib.auth.client";
import { drizzle } from "@-/com.npmjs.drizzle-orm/expo-sqlite-async";
import { createPerformanceLogger } from "@-/dev.improve.haniverse.lib.database.logger";
import { wrapActorDatabase } from "#wrap-actor-database";
import type {
  ActorDatabaseConnection,
  ActorDatabaseHandle,
  ActorLeaderStatus,
  ActorRawDatabaseHandle,
} from "#types";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

// Web runs ONE SQLite engine (`@tursodatabase/sync-wasm`) in both states, but in
// TWO separate OPFS files:
//
//   `actor.db` — sync mode ONLY. Always opened WITH a url + `bootstrapIfEmpty`,
//                and only ever as a fresh/empty file.
//   `guest.db` — signed-out ONLY. Always opened WITHOUT a url, and never
//                `connect()`ed to a server.
//
// They must never converge on one path. Handing the sync engine a non-empty file
// that was never connected yields a database that reads fine and reports `push()`
// success while syncing in NEITHER direction — silently. Separate paths make that
// state unreachable by construction (see `open-guest-turso-connection.web.ts`).
//
// Content and static info data live in their own remote (Cloudflare-Worker-
// backed) connections (`database/content` and `database/static`) — actor doesn't
// ATTACH anything.
const ACTOR_OPFS_PATH = "actor.db";
export const guestReplayRunsDuringOpen = false;
/** OPFS name of the signed-out (guest) database. Exported for the sign-up replay. */
export const GUEST_OPFS_PATH = "guest.db";

// Both databases go through the multi-tab LEADER PROXY unconditionally: one
// tab's worker owns the file's exclusive OPFS access handle and every other tab
// proxies its statements to it. There is no direct-open path on web any more —
// the `EXPO_PUBLIC_HANIVERSE_WEB_LEADER_{GUEST,ACTOR}_DB` flags that used to gate this
// were removed, so reverting the mechanism is a
// code revert, not an env change. The direct openers still exist, but only the
// leader worker itself calls them (`open-leader-turso-resource.web.ts`).

// Mirrors the native engine: when a device-account is present the actor DB is
// opened AS the Turso sync `Database` (sync-wasm), so every write flows through
// Turso's per-connection CDC and is pushable. The raw `tursoDb` (pull/push) is
// stashed here so the Settings screen's manual Save/Load actions drive the SAME
// connection the app writes through.
let activeActorTursoSync: ActorTursoSyncConnection["tursoDb"] | null = null;

/**
 * The live PROXIED actor connection, while signed in.
 *
 * Tracked separately from `activeActorTursoSync` because the sign-in wipe needs
 * something that handle cannot express: `runWithEngineClosed`, which closes the
 * LEADER's engine while it keeps the election lock. Without holding the lock
 * across the wipe, another tab could elect itself and re-open `actor.db` in the
 * gap — and `wipeTursoDatabaseFiles` would then silently no-op, because OPFS
 * refuses `removeEntry` on a file with a live sync access handle.
 */
let activeProxiedActorConnection: ProxiedTursoConnection | null = null;

// DEV-ONLY readiness beacon for the Safari leg of the cloud-sync Maestro flow. iOS 26.5 does not
// expose Safari's DOM subtree to XCUITest, so the flow cannot wait on visible app content without
// racing the post-sign-in reload. Signal the existing same-origin dev endpoint only after the real
// Turso handle is usable; production returns 404 and this call is skipped entirely there.
function pokeActorDbReadyBeacon(): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  try {
    void fetch("/api/actor-db-ready", { method: "POST" }).catch(() => {});
  } catch {
    /* best-effort */
  }
}

export function getActiveActorTursoSync(): ActorTursoSyncConnection["tursoDb"] | null {
  return activeActorTursoSync;
}

// ── LEADER STATUS STORE (plan §8) ─────────────────────────────────────────────
// A tiny external store over "which connection is this tab on, and is it the
// leader?" for `useLeaderResourceStatus()`. One slot, not one per database: a
// tab holds exactly one actor connection at a time (guest OR sync), and the
// in-place sign-in reopen replaces it, so the previous subscription is dropped
// whenever a new connection is tracked.
let activeActorLeaderStatus: ActorLeaderStatus | null = null;
let unsubscribeActiveActorLeaderStatus: (() => void) | null = null;
const actorLeaderStatusListeners = new Set<() => void>();

function publishActorLeaderStatus(next: ActorLeaderStatus | null): void {
  activeActorLeaderStatus = next;
  for (const listener of actorLeaderStatusListeners) {
    listener();
  }
}

function trackActorLeaderStatus(
  database: ActorLeaderStatus["database"],
  resourceId: string,
  proxied: ProxiedTursoConnection,
): void {
  unsubscribeActiveActorLeaderStatus?.();
  const snapshotOf = (status: ProxiedTursoConnection["status"]): ActorLeaderStatus => ({
    database,
    // Live getter, deliberately non-reactive — see `ActorLeaderStatus`.
    get proxiedQueryCount() {
      return proxied.queryCount;
    },
    resourceId,
    status,
    tabId: proxied.tabId,
  });
  unsubscribeActiveActorLeaderStatus = proxied.subscribeStatus((status) => {
    publishActorLeaderStatus(snapshotOf(status));
  });
  publishActorLeaderStatus(snapshotOf(proxied.status));
  // DEV-ONLY: let a Playwright page read this tab's standing and query count
  // without a React tree (`multi-tab-leader.e2e.spec.ts` → query-volume
  // baseline). A getter rather than the object, so it never goes stale.
  if (process.env.NODE_ENV !== "production") {
    Reflect.set(globalThis, "__haniverseActorLeaderStatus", getActorLeaderStatus);
  }
}

/** Snapshot for `useSyncExternalStore`; `null` until a connection is open. */
export function getActorLeaderStatus(): ActorLeaderStatus | null {
  return activeActorLeaderStatus;
}

export function subscribeActorLeaderStatus(listener: () => void): () => void {
  actorLeaderStatusListeners.add(listener);
  return () => {
    actorLeaderStatusListeners.delete(listener);
  };
}

/**
 * Web parity for the native `markActorDatabaseConnectionClosed`. Called by the shared
 * `use-actor-database.ts` state machine once a connection is confirmed closed; here it
 * only nulls the active handles. Keeps one import surface across platforms.
 */
export function markActorDatabaseConnectionClosed(): void {
  activeActorTursoSync = null;
  activeProxiedActorConnection = null;
}

/**
 * Detach this tab from the actor leader, if the leader path opened one.
 *
 * ── WHY THIS EXISTS, AND WHY `closeAsync` MUST NOT DO IT DURING SIGN-IN ───────
 * The sign-in sequence is
 *
 *     closeCachedActorDatabaseConnection()  →  resetActorDatabaseForSignIn()
 *                                           →  reopenActorDatabaseConnectionInSyncMode()
 *
 * and the plan's §5.3 assumed the reset could ask a LIVE leader to pause. It
 * cannot, if the close has already run the proxy's `closeAsync` — that detaches
 * this tab AND terminates its worker, so by the time the wipe runs there is no
 * client left to send `closeEngineHoldingLeadership` through. Worse, detaching
 * relinquishes leadership, so another tab immediately elects itself and RE-OPENS
 * `actor.db` — putting a live OPFS handle on precisely the file about to be
 * deleted, which makes `wipeTursoDatabaseFiles` silently no-op (it swallows the
 * `removeEntry` failure) and leaves sign-in reporting "Already up to date"
 * against a stale local database.
 *
 * So the proxy's `closeAsync` is deliberately a NO-OP for the actor connection
 * during this window, and the detach happens HERE instead — after the wipe and
 * reopen have completed under the leader's retained election lock.
 */
export async function detachProxiedActorConnection(): Promise<void> {
  const proxied = activeProxiedActorConnection;
  activeProxiedActorConnection = null;
  activeActorTursoSync = null;
  await proxied?.close();
}

/**
 * Wipe `actor.db` (and every sync-wasm sidecar) so the next sync-mode open
 * BOOTSTRAPS the account's cloud data from an empty file. Called on SIGN-IN
 * (existing account → the server is the source of truth), before the reopen.
 *
 * Why an explicit wipe is still needed even though the open now passes
 * `bootstrapIfEmpty: true`: that flag only fires when the local file is EMPTY. A
 * file left over from a previous session is non-empty, so bootstrap would be
 * skipped and sign-in would report "Already up to date" against an empty deck.
 * The wipe restores the empty-file precondition the flag depends on.
 *
 * Deliberately does NOT touch `guest.db`. Sign-in discards local guest study by
 * design (the remote wins), but the guest file is left intact so the pre-sign-in
 * export can still read it, and so a failed sign-in doesn't destroy data. Sign-UP
 * takes the replay path instead and needs the guest file preserved outright.
 *
 * Callers must also drop the actor migration fingerprint so the reopened DB
 * re-migrates cleanly (see `reloadForActorSyncIfNeeded`).
 */
export async function resetActorDatabaseForSignIn(): Promise<void> {
  // MULTI-TAB (§5.3). With the leader path on, "close the connection first" is no
  // longer something this tab can do alone: the OPFS handle belongs to whichever
  // tab leads, which may not be this one. `runWithEngineClosed` asks the LEADER
  // to close its engine while KEEPING the election lock, so:
  //   - the handle blocking `removeEntry` is released, and
  //   - no other tab can elect itself and re-open the file mid-wipe (which would
  //     make `wipeTursoDatabaseFiles` silently no-op — it swallows the failure).
  // Other tabs queue on `leader-paused` and flush on `leader-resumed`; leadership
  // never changes, so nobody re-elects.
  const proxied = activeProxiedActorConnection;
  if (proxied !== null) {
    await proxied.runWithEngineClosed(async () => {
      const removed = await wipeTursoDatabaseFiles(ACTOR_OPFS_PATH);
      logger(import.meta.location).log(
        `[actor-db] sign-in reset (leader-paused) removed ${arrayLength(removed)} OPFS entr${hasLength(removed, 1) ? "y" : "ies"}`,
      );
    });
    // The wipe is done and the leader has reopened onto the now-empty file (which
    // is what makes `bootstrapIfEmpty` fire and pull the account's cloud data).
    // Detach only NOW: this tab's next open re-attaches through
    // `createActorDatabaseConnection`, and detaching any earlier would have
    // handed leadership away mid-wipe.
    await detachProxiedActorConnection();
    return;
  }
  const removed = await wipeTursoDatabaseFiles(ACTOR_OPFS_PATH);
  logger(import.meta.location).log(
    `[actor-db] sign-in reset removed ${arrayLength(removed)} OPFS entr${hasLength(removed, 1) ? "y" : "ies"}`,
  );
}

export async function createActorDatabaseConnection(
  // Accepted for parity with native. On web the engine, and therefore its connection lock, lives in
  // the OPFS leader worker; a damaged connection there is replaced by ending that leader's term
  // (`open-leader-turso-resource`), and this tab's proxied calls fail over with it.
  _options: { onDamaged?: (error: unknown) => void } = {},
): Promise<AnySQLiteDatabase> {
  let deviceAccount = getDeviceAccount();
  if (isLegacyDeviceAccount(deviceAccount)) {
    // Stored by a build from before the sync gateway protocol: its credential is not accepted and
    // its replica predates the baseline. Drop both; the session is untouched, so the device is
    // provisioned again and bootstraps from the account's cloud copy. (If another tab still holds
    // the file the wipe is a no-op, and the leader starts the foreign replica over at its open.)
    const removed = await wipeTursoDatabaseFiles(ACTOR_OPFS_PATH);
    logger(import.meta.location).warn("actor-db:legacy-account-discarded", { removed });
    setDeviceAccount(null);
    deviceAccount = null;
  }
  // Resolve the sync URL: the persisted cloud URL in prod, the LIVE dev stack's inlined URL in dev
  // (the account no longer persists a dev URL — see `resolveActorSyncUrl` / `deviceAccountSchema`).
  // `null` → no reachable URL, so open local-only instead of connecting to a bogus/stale port.
  const syncUrl = deviceAccount == null ? null : resolveActorSyncUrl(deviceAccount);

  if (deviceAccount != null && syncUrl != null) {
    // Resolved now, from the origin this page talks to; never a stored address.
    const gatewayUrl = resolveActorSyncGatewayUrl(getBetterAuthBaseUrl());
    // Signed in: the leader's worker opens `actor.db` via the Turso sync engine
    // (CDC-capturing, single writer) and every tab proxies to it. The open's own
    // connect ladder (12s x 3 attempts, wiping between) and the proxy's 60s
    // handover budget bound it, so a stalled connect surfaces as an error rather
    // than a hung screen — see `ACTOR_HANDOVER_BUDGET_MS` for why the ladder's
    // error is the one meant to surface first.
    return openProxiedWebActorSyncConnection({
      authToken: deviceAccount.tursoDbAuthToken,
      gatewayUrl,
      url: syncUrl,
    });
  }

  activeActorTursoSync = null;
  logger(import.meta.location).info("actor-db:open", { mode: "signed-out-local-only" });
  // Guest mode deliberately does NOT poke the actor-db-ready beacon: that signal
  // means "the SYNC connection is live", and the Maestro Safari leg polls it to
  // know sign-in finished. Firing it here would let that poll exit on a guest
  // launch.
  return createPerformanceLogger(
    SQLite.attachSqliteChangeFeed(await openGuestWebActorConnection(), GUEST_OPFS_PATH),
    "actorDb",
  );
}

/**
 * The leader migrated the database to ITS build when it opened it. This tab may carry a newer
 * build, so it asks for the rest; tabs on different builds then share one leader and converge on
 * the newest schema. A tab that cannot get its schema must not keep the connection (and with it,
 * possibly, the leadership).
 */
async function applyThisBuildsMigrations(proxied: ProxiedTursoConnection): Promise<void> {
  try {
    await proxied.updateSchema(actorMigrations.migrations);
  } catch (error) {
    await proxied.close().catch(() => {});
    throw error;
  }
}

/**
 * Signed-in connection through the multi-tab LEADER PROXY.
 *
 * One tab's worker owns `actor.db`'s exclusive OPFS handle and its single Turso
 * connection; every other tab proxies its statements to it. That is what makes a
 * second signed-in tab possible at all — but it also STRENGTHENS the CDC
 * invariant rather than merely preserving it:
 *
 * `capture_data_changes` is PER-CONNECTION. Today, within one tab, every actor
 * write already flows through one Turso connection so it can be pushed. With the
 * proxy, every write from EVERY tab flows through that same single connection,
 * and `push()` is itself an RPC onto it — so the CDC log being pushed is exactly
 * the log every tab's writes were captured into. Without the leader, two tabs
 * would mean two connections, two CDC logs, and a `push()` from one tab that
 * silently omitted the other's writes.
 *
 * `activeActorTursoSync` is set to the PROXIED `{ close, pull, push }` for the
 * same reason it is set on the direct path: the Settings screen's manual
 * Save/Load actions must drive the connection the app writes through.
 */
async function openProxiedWebActorSyncConnection(
  sync: ProxiedTursoSyncOptions,
): Promise<AnySQLiteDatabase> {
  try {
    const proxied = await openProxiedActorTursoConnection(ACTOR_LEADER_RESOURCE_ID, sync);
    await applyThisBuildsMigrations(proxied);
    activeProxiedActorConnection = proxied;
    trackActorLeaderStatus("actor", ACTOR_LEADER_RESOURCE_ID, proxied);
    activeActorTursoSync =
      proxied.sync === null
        ? null
        : {
            // `close` on the proxy detaches THIS tab (relinquishing leadership if
            // it holds it) rather than closing the shared engine — closing it
            // would rip the database out from under every other tab.
            close: async () => proxied.close(),
            pull: proxied.sync.pull,
            push: proxied.sync.push,
            stats: proxied.sync.stats,
          };
    logger(import.meta.location).info("actor-db:open", {
      isLeader: proxied.isLeader,
      mode: "sync-leader-proxy",
      url: sync.url,
    });
    pokeActorDbReadyBeacon();
    return createPerformanceLogger(
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the leader proxy implements the expo-sqlite surface
      SQLite.attachSqliteChangeFeed(proxied.connection as unknown as AnySQLiteDatabase, "actor.db"),
      "actorDb",
    );
  } catch (error) {
    // Same rethrow policy as the direct sync path: NO local-only fallback. A
    // signed-in user's writes landing in `guest.db` would never sync, silently.
    logger(import.meta.location).info("actor-db:open", {
      error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
      mode: "sync-leader-proxy-THREW",
    });
    activeProxiedActorConnection = null;
    activeActorTursoSync = null;
    throw error;
  }
}

/**
 * Signed-out (guest) connection: the SAME Turso engine as the sync path, opened
 * local-only against `guest.db` (no url, no auth) by the leader's worker
 * (`openGuestTursoConnection`, which applies the innermost `SQLITE_BUSY`
 * containment and the connection lock there), and reached from every tab
 * through the multi-tab LEADER PROXY. One tab's worker owns the exclusive OPFS
 * handle and every other tab proxies its queries to it, so a second tab no
 * longer dies on "Access Handles cannot be created …".
 */
/** `guest.db` as a reader for the sign-up carry (see `carry-guest-study.ts`); never `null` on web. */
export async function openGuestHandoffForExport(): Promise<{
  all: (sql: string) => Promise<unknown[]>;
  close: () => Promise<void>;
} | null> {
  const guest = await openGuestWebActorConnection();
  return { all: (sql) => guest.getAllAsync(sql), close: () => guest.closeAsync() };
}

async function openGuestWebActorConnection(): Promise<AnySQLiteDatabase> {
  const proxied = await openProxiedGuestTursoConnection(GUEST_LEADER_RESOURCE_ID);
  await applyThisBuildsMigrations(proxied);
  trackActorLeaderStatus("guest", GUEST_LEADER_RESOURCE_ID, proxied);
  logger(import.meta.location).info("actor-db:open", {
    isLeader: proxied.isLeader,
    mode: "guest-leader-proxy",
  });
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the leader proxy implements the expo-sqlite surface
  return proxied.connection as unknown as AnySQLiteDatabase;
}

function isActorRawDatabase(value: unknown): value is ActorRawDatabaseHandle {
  return isAnyObject(value);
}

export function getActorRawDatabase(connection: ActorDatabaseConnection): ActorRawDatabaseHandle {
  const db = drizzle(connection, {
    relations: actorRelations,
    schema: actorSchema,
  });
  if (!isActorRawDatabase(db)) {
    throw new TypeError("Failed to open actor database");
  }
  return db;
}

export function getActorDatabase(connection: ActorDatabaseConnection): ActorDatabaseHandle {
  return wrapActorDatabase(getActorRawDatabase(connection));
}

/**
 * Sign-out on web keeps its two-file design for now: the account's `actor.db` is
 * wiped at the NEXT sign-in (`resetActorDatabaseForSignIn`), and signed-out study
 * goes to the separate `guest.db`. Native wipes on sign-out (Leon's decision,
 * 2026-09-17); collapsing web to one file is Phase 2 of
 * `unified-turso-actor-db-plan.md`.
 */
export function resetActorDatabaseForSignOut(): Promise<void> {
  return Promise.resolve();
}

/**
 * Recovery from an unopenable actor database. Web has no generation manifest or quarantine; its
 * reset is the sign-in wipe, which re-bootstraps the account from the cloud on the next open.
 */
export function resetActorDatabaseForRecovery(): Promise<void> {
  return resetActorDatabaseForSignIn();
}

/**
 * Web needs no stash before the sign-up reopen: guest study already lives in its
 * own `guest.db`, which the sqldiff replay reads after the account bootstraps.
 * Returns `false` (nothing moved) so shared callers can log uniformly.
 */
export function stashActorDatabaseForGuestReplay(): Promise<boolean> {
  return Promise.resolve(false);
}

/**
 * Web keeps `guest.db` after a successful replay (the pre-sign-in export path can
 * still read it, and the guest connection reopens onto it after sign-out).
 */
export function discardGuestHandoffDatabase(): Promise<void> {
  return Promise.resolve();
}
